/*
 * Joiners, leavers and approvers away (migration 113) - the words the People
 * page, Settings and the approver picker share, so one screen cannot say a
 * date or a state differently from another. Pure: no React, no requests.
 */
import { dayLabel } from './when';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/** '2026-11-05' -> '5 November' (with the year when it is not this one). */
export function dayWords(day, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return '';
  const base = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  return Number(m[1]) === now.getFullYear() ? base : `${base} ${m[1]}`;
}

/** An absence in a phrase: "away until 5 November" / "away 10 to 12 November". */
export function awayPhrase(a, now = new Date()) {
  if (!a) return '';
  if (a.now) return `away until ${dayWords(a.until, now)}`;
  return a.from === a.until ? `away on ${dayWords(a.from, now)}` : `away ${dayWords(a.from, now)} to ${dayWords(a.until, now)}`;
}

/** An approver as the picker shows them: "Maria Zammit — away until 5 November, Joe covering". */
export function approverOption(a, now = new Date()) {
  const name = a.display_name || a.name || a.email;
  if (!a.away) return name;
  return `${name} — ${awayPhrase(a.away, now)}${a.away.now && a.away.delegate_name ? `, ${a.away.delegate_name} decides` : ''}`;
}

export const KIND_LABEL = { joiner: 'Joiner', leaver: 'Leaver' };
export const DAY_LABEL = { joiner: 'Starts', leaver: 'Leaves' };
export const SOURCE_LABEL = {
  manual: 'Started by hand',
  user_added: 'Started when they were added',
  deactivated: 'Started when they were deactivated',
  inactive: 'Started from Not signed in',
  entra: 'Started by the directory sync'
};

/** A step's due date, against today: 'overdue' | 'today' | 'soon' | null. */
export function dueState(step, today) {
  if (!step?.due_on || step.state !== 'todo' || !today) return null;
  if (step.due_on < today) return 'overdue';
  if (step.due_on === today) return 'today';
  return null;
}

/** A due date as a chip says it: Today / Tomorrow / Fri 6 Nov. */
export const dueWords = (day) => (day ? dayLabel(day) : 'No date');

/** Steps grouped by section, in their order; a step with no section goes under "Other". */
export function bySection(steps) {
  const out = [];
  const index = new Map();
  for (const s of steps || []) {
    const name = s.section || 'Other';
    if (!index.has(name)) { index.set(name, out.length); out.push({ name, steps: [] }); }
    out[index.get(name)].steps.push(s);
  }
  return out;
}

/** "3 of 15" and a fraction for the progress bar. */
export function progress(run) {
  const total = Number(run?.steps_total) || 0;
  const done = Number(run?.steps_done) || 0;
  return { total, done, fraction: total ? done / total : 0, label: `${done} of ${total}` };
}

/** Today in Malta, 'YYYY-MM-DD' - the server's dates are Malta dates. */
export const maltaToday = (now = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Malta', year: 'numeric', month: '2-digit', day: '2-digit'
}).format(now);
